'use client';

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface MenuChoice {
  value: string;
  label: string;
  /** Set at the row's right: a count, a state, whatever tells them apart. */
  note?: ReactNode;
}

export interface MenuAction {
  label: string;
  onClick?: () => void;
  /** The sentence the short label leaves out. */
  title?: string;
  disabled?: boolean;
  /** Destructive, drawn in the danger colour. */
  danger?: boolean;
  /** An action that is really a choice: its own list opens beside the menu,
   *  the way a submenu does, instead of a window for one field. */
  choices?: readonly MenuChoice[];
  onChoose?: (value: string) => void;
}

/** Between a panel and what opened it, and between a panel and the window. */
const GAP = 6;
const EDGE = 8;

/** A list to the side of the row that opened it. */
function submenuPlace(row: DOMRect, menu: DOMRect, box: { width: number; height: number }) {
  const edge = document.documentElement.clientWidth;
  // To the left, because this menu hangs at the right of a bar; to the right
  // when the window has no room on that side.
  const left = menu.left - GAP - box.width;
  const at = left >= EDGE ? left : Math.min(menu.right + GAP, edge - box.width - EDGE);
  return {
    top: Math.max(EDGE, Math.min(row.top - 5, window.innerHeight - EDGE - box.height)),
    left: Math.max(EDGE, at),
  };
}

/** The actions a bar has room to name, and the rest behind one button: a row
 *  of six, where the sixth is destructive and the first is the one anybody
 *  presses, is a row nobody reads past the first. */
export function ActionMenu({
  actions,
  label = 'More actions',
  children,
}: {
  actions: readonly MenuAction[];
  label?: string;
  /** What the trigger shows. Three dots by default. */
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  /* Which row's own list is out, if any. The menu stays where it is under it:
     a child list beside its parent is read as belonging to the row it came
     from, where a menu that turns into another one is read as a mistake. */
  const [choosing, setChoosing] = useState<string | null>(null);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const [childPlace, setChildPlace] = useState<{ top: number; left: number } | null>(null);
  /* Typed into the child list, not carried between them: a filter left over
     from the last list is a list that looks empty for no reason. */
  const [needle, setNeedle] = useState('');
  const search = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const child = useRef<HTMLDivElement>(null);
  const rows = useRef(new Map<string, HTMLButtonElement>());
  const child_ = actions.find((one) => one.label === choosing) ?? null;
  const shown = (child_?.choices ?? []).filter((one) =>
    one.label.toLowerCase().includes(needle.trim().toLowerCase()),
  );
  /* A filter only where a list is long enough to need one: over a team of two
     dozen it is the difference between reading and hunting. */
  const filtered = (child_?.choices?.length ?? 0) > 7;

  /* Fixed and portalled, like every other panel this project opens: inside a
     card with its own scroll, an absolutely placed menu is clipped by it. */
  useLayoutEffect(() => {
    if (!open) return;
    const put = () => {
      const from = trigger.current?.getBoundingClientRect();
      if (!from) return;
      const box = pop.current?.getBoundingClientRect();
      const width = box?.width ?? 200;
      const height = box?.height ?? 0;
      const below = from.bottom + GAP + height < window.innerHeight - EDGE;
      // Hung off the button's right edge: these buttons sit at the end of a
      // bar, and a menu opening rightwards leaves the window.
      setPlace({
        top: below ? from.bottom + GAP : Math.max(EDGE, from.top - GAP - height),
        left: Math.max(
          EDGE,
          Math.min(from.right - width, document.documentElement.clientWidth - width - EDGE),
        ),
      });
      const row = choosing ? rows.current.get(choosing)?.getBoundingClientRect() : null;
      const menu = pop.current?.getBoundingClientRect();
      const kid = child.current?.getBoundingClientRect();
      if (row && menu && kid) setChildPlace(submenuPlace(row, menu, kid));
    };
    put();
    const again = requestAnimationFrame(put);
    window.addEventListener('scroll', put, true);
    window.addEventListener('resize', put);
    return () => {
      cancelAnimationFrame(again);
      window.removeEventListener('scroll', put, true);
      window.removeEventListener('resize', put);
    };
  }, [open, actions.length, choosing]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (pop.current?.contains(target) || child.current?.contains(target)) return;
      if (trigger.current?.contains(target)) return;
      setOpen(false);
      setChoosing(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      /* Swallowed while this is open, and caught before anybody else: the
         screen behind it takes Escape as "close the whole thing", so one press
         was shutting the case rather than the menu on top of it. */
      event.stopPropagation();
      if (choosing) {
        setChoosing(null);
        return;
      }
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, choosing]);

  /* The filter takes the keyboard the moment its list opens: the reason to
     open a list of two dozen names is to type one. */
  useEffect(() => {
    if (!choosing) return;
    // After the panel exists, not with it: focusing in the same tick as the
    // state that mounts it focuses nothing at all.
    const at = requestAnimationFrame(() => search.current?.focus());
    return () => cancelAnimationFrame(at);
  }, [choosing]);

  if (actions.length === 0) return null;

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`btn btn-sm action-menu-open${open ? ' is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => {
          setChoosing(null);
          setOpen((was) => !was);
        }}
      >
        {children ?? <span aria-hidden>{'···'}</span>}
      </button>

      {open
        && typeof document !== 'undefined'
        && createPortal(
          <>
            <div
              ref={pop}
              className="action-menu"
              role="menu"
              style={
                place ? { top: place.top, left: place.left } : { top: 0, left: -9999, visibility: 'hidden' }
              }
            >
              {actions.map((one) => (
                <button
                  key={one.label}
                  ref={(node) => {
                    if (node) rows.current.set(one.label, node);
                    else rows.current.delete(one.label);
                  }}
                  type="button"
                  role="menuitem"
                  aria-haspopup={one.choices ? 'menu' : undefined}
                  aria-expanded={one.choices ? choosing === one.label : undefined}
                  className={`action-menu-item${one.danger ? ' is-danger' : ''}${
                    choosing === one.label ? ' is-open' : ''
                  }`}
                  disabled={one.disabled}
                  title={one.title}
                  // A child list opens where the pointer already is, and a
                  // pointer moving down the menu closes the last one.
                    onMouseEnter={() => {
                    setNeedle('');
                    setChoosing(one.choices && !one.disabled ? one.label : null);
                  }}
                  onClick={() => {
                    /* Opens, never toggles: the pointer has already hovered
                       this row on its way to clicking it, so a toggle shut the
                       list the hover had just opened. */
                    if (one.choices) {
                      setChoosing(one.label);
                      return;
                    }
                    setOpen(false);
                    setChoosing(null);
                    one.onClick?.();
                  }}
                >
                  {one.label}
                  {one.choices && <span className="action-menu-more" aria-hidden>{'›'}</span>}
                </button>
              ))}
            </div>

            {child_ && (
              <div
                ref={child}
                className="action-menu is-child"
                role="menu"
                style={
                  childPlace
                    ? { top: childPlace.top, left: childPlace.left }
                    : { top: 0, left: -9999, visibility: 'hidden' }
                }
              >
                <span className="action-menu-head">{child_.label}</span>
                {filtered && (
                  /* The panel's own search, in the shape every other list in
                     the product searches in: a glass, a line under it, and no
                     box of its own inside a box. */
                  <div className="action-menu-searchwrap">
                    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
                      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
                      <path
                        d="M10.5 10.5L14 14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </svg>
                    <input
                      ref={search}
                      className="action-menu-search"
                      placeholder="Type to filter"
                      aria-label={`Filter ${child_.label.toLowerCase()}`}
                      value={needle}
                      onChange={(event) => setNeedle(event.target.value)}
                    />
                  </div>
                )}
                {filtered && shown.length === 0 && (
                  <span className="action-menu-head">{'Nobody by that name'}</span>
                )}
                {shown.map((one) => (
                  <button
                    key={one.value}
                    type="button"
                    role="menuitem"
                    className="action-menu-item"
                    onClick={() => {
                      setOpen(false);
                      setChoosing(null);
                      child_.onChoose?.(one.value);
                    }}
                  >
                    {one.label}
                    {one.note !== undefined && <span className="action-menu-note">{one.note}</span>}
                  </button>
                ))}
              </div>
            )}
          </>,
          document.body,
        )}
    </>
  );
}
